import {
  formatChatThreadTimestamp,
  shouldShowChatTimestamp,
} from '@features/ai-chat/ai-chat-timestamp.helpers';
import type { IComment } from '@shared/models/comment.model';
import type {
  ProjectEvent,
  ProjectEventActivity,
} from '@shared/models/project-event.model';
import {
  footprintStoreyIds,
  projectEventChangedAt,
} from '@shared/helpers/project-events/project-events.helpers';

/** Timing and grouping fields a thread row needs, shared by comments and events. */
export type ThreadEntry = Pick<IComment, 'id' | 'owner' | 'created_at'>;

export type CommentThreadItem =
  | { kind: 'comment'; comment: IComment }
  | {
      kind: 'activity';
      /** Built from the project event; `owner` is the grouping key. */
      comment: ThreadEntry;
      activity: ProjectEventActivity;
      /** User the event belongs to. For agent events, the user who asked. */
      userId: string;
      agent: boolean;
      /** Last change, including edits merged in later; drives unread. */
      changedAt: string;
    };

/** Agent events group apart from the same user's own edits. */
export const eventThreadItem = (
  event: ProjectEvent,
): Extract<CommentThreadItem, { kind: 'activity' }> => {
  const agent = event.actor_kind === 'agent';
  return {
    kind: 'activity',
    comment: {
      id: event.id,
      owner: agent ? `agent:${event.actor_id}` : event.actor_id,
      created_at: event.created_at,
    },
    activity: event.activity,
    userId: event.actor_id,
    agent,
    changedAt: projectEventChangedAt(event),
  };
};

/**
 * Lowest storey in a run is the link target. A span reads `storey 3-6`, one
 * storey its user-set name or `storey 3`. Without ids from a collapsed run,
 * the storeys one merged event covers.
 */
export const footprintStoreyPhrase = (
  activity: { storeyId: string; storeyIds?: readonly string[] },
  names: {
    /** User-set storey names only; unnamed storeys read by number. */
    storeys: Map<string, string>;
    storeyNumbers: Map<string, number>;
  },
  storeyIds: readonly string[] | undefined,
): { text: string; storeyId: string } | undefined => {
  const ids = storeyIds?.length ? storeyIds : footprintStoreyIds(activity);
  const ranked = [...new Set(ids)].flatMap((id) => {
    const number = names.storeyNumbers.get(id);
    if (number === undefined) return [];
    return [{ id, number }];
  });
  ranked.sort((left, right) => left.number - right.number);
  const lowest = ranked[0];
  const highest = ranked[ranked.length - 1];
  if (!lowest || !highest) return;
  if (lowest.number === highest.number) {
    return {
      text: names.storeys.get(lowest.id) ?? `storey ${lowest.number}`,
      storeyId: lowest.id,
    };
  }
  return {
    text: `storey ${lowest.number}-${highest.number}`,
    storeyId: lowest.id,
  };
};

/**
 * Groups that stay one summary row, with the details in a tooltip, instead of
 * getting a caret: expanding them would only repeat the same sentence
 * (Joel's UX requirements: "Grupper som inte expanderas").
 */
const SUMMARY_ONLY_KINDS: ReadonlySet<ProjectEventActivity['kind']> = new Set([
  'action-created',
  'baseline-edited',
  'building-moved',
  'building-created',
  'storey-footprint-edited',
  'storey-added',
  'project-location',
]);

export const isSummaryOnlyActivity = (
  kind: ProjectEventActivity['kind'],
): boolean => SUMMARY_ONLY_KINDS.has(kind);

/**
 * Clock separator for one History row.
 * `previousVisibleCreatedAt` is the previous row that is still shown. Hidden
 * activities have to be removed first, or they take the separator from the
 * first visible row in the group.
 */
export const historyTimestampLabel = (
  createdAt: string,
  previousVisibleCreatedAt: string | undefined,
  now: Date,
): string | undefined => {
  if (!shouldShowChatTimestamp(createdAt, previousVisibleCreatedAt)) return;
  return formatChatThreadTimestamp(createdAt, now);
};

/** Consecutive comments share one bubble until author, activity or clock group changes.
 * Only the first entry returns the run; subsequent entries are rendered by it.
 */
export const commentBubbleRun = (
  thread: readonly CommentThreadItem[],
  index: number,
): readonly IComment[] => {
  const item = thread[index];
  if (item?.kind !== 'comment') return [];
  const previous = thread[index - 1];
  if (
    previous?.kind === 'comment' &&
    previous.comment.owner === item.comment.owner &&
    !shouldShowChatTimestamp(
      item.comment.created_at,
      previous.comment.created_at,
    )
  )
    return [];
  const comments = [item.comment];
  for (let nextIndex = index + 1; nextIndex < thread.length; nextIndex++) {
    const next = thread[nextIndex];
    const previous = thread[nextIndex - 1];
    if (
      next?.kind !== 'comment' ||
      next.comment.owner !== item.comment.owner ||
      shouldShowChatTimestamp(
        next.comment.created_at,
        previous?.comment.created_at,
      )
    )
      break;
    comments.push(next.comment);
  }
  return comments;
};
